/**
 * Survill Device Authentication Service
 * Handles communication with local device auth agent
 * Version 2.0 - Challenge-Response Authentication
 */

const AGENT_BASE_URL = 'https://localhost:8765';

/**
 * Generate signature for challenge using device credentials
 * This must match the backend signature algorithm
 */
function generateSignature(challenge, deviceKey, macAddress) {
  const data = `${challenge}:${deviceKey}:${macAddress}`;
  
  // Use Web Crypto API for SHA-256
  return crypto.subtle.digest('SHA-256', new TextEncoder().encode(data))
    .then(hashBuffer => {
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    });
}

/**
 * Check if the device auth agent is running
 * @returns {Promise<boolean>}
 */
export async function isAgentRunning() {
  try {
    const response = await fetch(`${AGENT_BASE_URL}/health`, {
      method: 'GET',
      mode: 'cors',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`Agent returned ${response.status}`);
    }
    
    const data = await response.json();
    console.log('Device agent health check:', data);
    return data.status === 'running';
    
  } catch (error) {
    console.error('Device agent not reachable:', error);
    return false;
  }
}

/**
 * Authenticate device using challenge-response flow (RECOMMENDED)
 * More secure than getDeviceCredentials()
 * 
 * @returns {Promise<Object>} Device credentials if authorized
 * @throws {Error} If authentication fails
 */
export async function authenticateDevice() {
  try {
    // Step 1: Request challenge from agent
    const challengeResponse = await fetch(`${AGENT_BASE_URL}/challenge`, {
      method: 'POST',
      mode: 'cors',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    if (!challengeResponse.ok) {
      throw new Error(`Failed to get challenge: ${challengeResponse.status}`);
    }
    
    const { challenge_id, challenge, expires_in } = await challengeResponse.json();
    
    console.log('Challenge received:', { challenge_id, expires_in });
    
    // Step 2: Get device credentials (legacy endpoint, used only for signing)
    const credsResponse = await fetch(`${AGENT_BASE_URL}/device-credentials`, {
      method: 'GET',
      mode: 'cors',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    if (!credsResponse.ok) {
      throw new Error(`Failed to get credentials: ${credsResponse.status}`);
    }
    
    const { device_key, mac_address } = await credsResponse.json();
    
    // Step 3: Generate signature for challenge
    const signature = await generateSignature(challenge, device_key, mac_address);
    
    // Step 4: Verify challenge with signature
    const verifyResponse = await fetch(`${AGENT_BASE_URL}/verify`, {
      method: 'POST',
      mode: 'cors',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        challenge_id,
        signature
      })
    });
    
    if (!verifyResponse.ok) {
      const errorData = await verifyResponse.json();
      throw new Error(errorData.error || 'Authentication failed');
    }
    
    const verifiedData = await verifyResponse.json();
    
    if (!verifiedData.authorized) {
      throw new Error('Device not authorized');
    }
    
    console.log('Device authenticated successfully:', {
      mac: verifiedData.mac_address,
      computer: verifiedData.computer_name
    });
    
    return verifiedData;
    
  } catch (error) {
    console.error('Device authentication error:', error);
    throw error;
  }
}

/**
 * Get device credentials directly (LEGACY - less secure)
 * Use authenticateDevice() instead for challenge-response flow
 * 
 * @returns {Promise<Object>} Device credentials
 * @throws {Error} If request fails
 */
// Cache agent unavailability: browsers block public sites from calling
// localhost (Private Network Access), so for most users this call can NEVER
// succeed. Without caching it added a failed round-trip to every token
// refresh. After one failure we skip retries for 5 minutes.
let agentUnavailableUntil = 0;
const AGENT_RETRY_COOLDOWN_MS = 5 * 60 * 1000;

export async function getDeviceCredentials() {
  if (Date.now() < agentUnavailableUntil) {
    throw new Error('Device agent unavailable (cached — skipping retry)');
  }
  try {
    const response = await fetch(`${AGENT_BASE_URL}/device-credentials`, {
      method: 'GET',
      mode: 'cors',
      credentials: 'include',
      signal: AbortSignal.timeout ? AbortSignal.timeout(1500) : undefined,
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`Agent returned ${response.status}`);
    }
    
    const data = await response.json();
    
    if (!data.authorized) {
      throw new Error(data.error || 'Device not authorized');
    }
    
    return data;
    
  } catch (error) {
    agentUnavailableUntil = Date.now() + AGENT_RETRY_COOLDOWN_MS;
    console.warn('Device agent unreachable; skipping retries for 5 minutes.');
    throw error;
  }
}

/**
 * Check if device key file exists on the local agent
 * @returns {Promise<boolean>}
 */
export async function verifyKeyExists() {
  try {
    const response = await fetch(`${AGENT_BASE_URL}/verify-key-exists`, {
      method: 'GET',
      mode: 'cors',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`Agent returned ${response.status}`);
    }
    
    const data = await response.json();
    return data.key_exists === true;
    
  } catch (error) {
    console.error('Failed to verify key exists:', error);
    return false;
  }
}

/**
 * Handle SSL certificate trust issues
 * Opens the health endpoint in a new tab so user can accept the cert
 */
export function promptCertificateTrust() {
  const healthUrl = `${AGENT_BASE_URL}/health`;
  window.open(healthUrl, '_blank');
  
  return new Promise((resolve) => {
    // Give user time to accept the certificate
    const checkInterval = setInterval(async () => {
      const isRunning = await isAgentRunning();
      if (isRunning) {
        clearInterval(checkInterval);
        resolve(true);
      }
    }, 2000);
    
    // Timeout after 60 seconds
    setTimeout(() => {
      clearInterval(checkInterval);
      resolve(false);
    }, 60000);
  });
}

/**
 * Complete device authentication flow with user guidance
 * Handles certificate trust, agent check, and authentication
 * 
 * @param {Object} options
 * @param {Function} options.onCertPrompt - Called when cert needs to be trusted
 * @param {Function} options.onAgentMissing - Called when agent is not installed
 * @param {Function} options.onSuccess - Called with device credentials on success
 * @param {Function} options.onError - Called with error message on failure
 * @returns {Promise<Object|null>} Device credentials or null
 */
export async function completeDeviceAuth(options = {}) {
  const {
    onCertPrompt = () => {},
    onAgentMissing = () => {},
    onSuccess = () => {},
    onError = () => {}
  } = options;
  
  try {
    // Check if agent is running
    const isRunning = await isAgentRunning();
    
    if (!isRunning) {
      // Might be a certificate issue - prompt user
      onCertPrompt();
      const trusted = await promptCertificateTrust();
      
      if (!trusted) {
        onAgentMissing();
        return null;
      }
    }
    
    // Authenticate with challenge-response
    const credentials = await authenticateDevice();
    onSuccess(credentials);
    return credentials;
    
  } catch (error) {
    onError(error.message);
    return null;
  }
}

export default {
  isAgentRunning,
  authenticateDevice,
  getDeviceCredentials,
  verifyKeyExists,
  promptCertificateTrust,
  completeDeviceAuth,
  AGENT_BASE_URL
};